import React, { useEffect, useState } from "react";
import { fetchMicrosoftConfig, startMicrosoftSignIn, microsoftErrorMessage } from "../../utils/microsoftSso";

/** Microsoft's four-square logo, as their sign-in branding guidelines require. */
const MicrosoftLogo = () => (
  <svg width="20" height="20" viewBox="0 0 21 21" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

/**
 * "Sign in with Microsoft". Renders nothing when the server has no Microsoft
 * sign-in configured (unless `alwaysShow`, used where it is the only option).
 */
export default function MicrosoftSignInButton({ loginHint, returnTo, label = "Sign in with Microsoft", alwaysShow = false, onConfig }) {
  const [enabled, setEnabled] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    fetchMicrosoftConfig().then((c) => {
      if (!alive) return;
      setEnabled(!!c.enabled);
      onConfig?.(c);
    });
    return () => {
      alive = false;
    };
  }, [onConfig]);

  if (!enabled && !alwaysShow) return null;

  const start = async () => {
    setBusy(true);
    setError("");
    try {
      await startMicrosoftSignIn({ loginHint, returnTo });
    } catch (e) {
      setBusy(false);
      setError(microsoftErrorMessage(e?.response?.data?.error?.code || "sso_not_configured"));
    }
  };

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={start}
        disabled={busy || enabled === false}
        className="w-full inline-flex items-center justify-center gap-3 rounded-md border border-[#8c8c8c] bg-white px-4 py-2.5 text-[15px] font-semibold text-[#5e5e5e] hover:bg-gray-50 disabled:opacity-60"
      >
        <MicrosoftLogo />
        {busy ? "Redirecting to Microsoft…" : label}
      </button>
      {error && (
        <p role="alert" className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
